import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type ArchivedDocument } from '../api';

/** One role-filtered snapshot for the company navigation and document list.
 * `pending=true` means unlinked to a cost, NOT NEW/IN_PROGRESS, so do not use it here.
 */
export function useCompanyDocuments(companyId: string, enabled = true, onArrival?: () => void) {
  const [snapshot, setSnapshot] = useState<{ companyId: string; documents: ArchivedDocument[] | null }>({ companyId, documents: null });
  const [error, setError] = useState<string | null>(null);
  const arrival = useRef(onArrival);
  arrival.current = onArrival;
  const loader = useRef<{ companyId: string; run: () => Promise<void> } | null>(null);
  const refresh = useCallback(async () => {
    // Old mutation callbacks must not refresh the newly opened company.
    if (loader.current?.companyId === companyId) await loader.current.run();
  }, [companyId]);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let request = 0;
    let seen: Set<string> | null = null;
    setSnapshot({ companyId, documents: null });
    setError(null);
    const run = async () => {
      const current = ++request;
      try {
        const next = await api.documents(companyId);
        if (!active || current !== request) return;
        const arrived = seen !== null && next.some((d) => d.status === 'NEW' && !seen!.has(d.id));
        // Remember all observed IDs for this visit: status changes and reappearing IDs
        // must not beep. The first successful snapshot establishes a quiet baseline.
        seen ??= new Set();
        next.forEach((d) => seen!.add(d.id));
        setSnapshot({ companyId, documents: next });
        setError(null);
        if (arrived) arrival.current?.();
      } catch (failure) {
        if (active && current === request) setError(failure instanceof Error ? failure.message : String(failure));
      }
    };
    loader.current = { companyId, run };
    void run();
    const timer = window.setInterval(() => void run(), 15000);
    return () => {
      active = false;
      loader.current = null;
      window.clearInterval(timer);
    };
  }, [companyId, enabled]);

  const documents = snapshot.companyId === companyId ? snapshot.documents : null;
  const pendingCount = documents?.filter((d) => d.status === 'NEW' || d.status === 'IN_PROGRESS').length ?? 0;
  return { documents, pendingCount, error, refresh };
}
